import { EChartsOption } from "echarts";
import {
  LimitPoint,
  RetroChart,
  flattenSegments,
} from "../../../models/chartData";

export type RetroLimitKey = "lineLimit" | "moduleLimit";

interface BuildRetroChartOptionsParams {
  chart: RetroChart;
  limits?: LimitPoint[];
  limitKey?: RetroLimitKey;
  activeSeries?: Record<string, boolean>;
  colors?: Record<string, string>;
  minDate?: number;
  maxDate?: number;
  zoomStart?: number;
  zoomEnd?: number;
  dynamicLevel?: number;
}

const MONTHS = [
  "янв",
  "фев",
  "мар",
  "апр",
  "май",
  "июн",
  "июл",
  "авг",
  "сен",
  "окт",
  "ноя",
  "дек",
];

export const buildRetroChartOptions = ({
  chart,
  limits = [],
  limitKey,
  activeSeries = {},
  colors = {},
  minDate,
  maxDate,
  zoomStart = 0,
  zoomEnd = 100,
  dynamicLevel,
}: BuildRetroChartOptionsParams): EChartsOption => {
  const series: any[] = chart.series.map((seriesItem) => {
    const { points } = flattenSegments(seriesItem.segments);
    return {
      name: seriesItem.name,
      type: "line",
      data: points,
      smooth: false,
      symbol: "circle",
      symbolSize: 3,
      sampling: "none",
      large: false,
      lineStyle: { width: 1.6 },
      color: colors[seriesItem.name],
      connectNulls: false,
    };
  });

  if (limitKey && limits.length > 0) {
    const limitData = limits
      .filter(
        (limit) =>
          limit[limitKey] !== undefined &&
          limit[limitKey] !== null,
      )
      .map((limit) => [limit.timestamp * 1000, limit[limitKey]]);

    if (limitData.length > 0) {
      series.push({
        name: "Лимит",
        type: "line",
        data: limitData,
        step: "end",
        symbol: "none",
        lineStyle: { color: "#d62728", width: 1, type: "dashed" },
        tooltip: { show: false },
        animation: false,
      });
    }
  }

  const legendSelected: Record<string, boolean> = {};
  chart.series.forEach((seriesItem) => {
    legendSelected[seriesItem.name] = activeSeries[seriesItem.name] ?? true;
  });

  const tooltipFormatter = (params: any[]) => {
    const first = params[0];
    const timestamp = first?.axisValue || first?.value?.[0];
    if (!timestamp) return "Нет данных";

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "Некорректное время";

    const pad = (num: number) => num.toString().padStart(2, "0");
    let result = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} `;
    result += `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}<br/>`;

    params.forEach((item) => {
      if (Array.isArray(item.value) && typeof item.value[1] === "number") {
        result += `${item.marker} ${item.seriesName}: ${Math.round(item.value[1])} А<br/>`;
      }
    });

    return result;
  };

  return {
    backgroundColor: "white",
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        snap: true,
        label: {
          precision: 0,
          backgroundColor: "#1894c3",
        },
      },
      confine: false,
      appendToBody: true,
      formatter: tooltipFormatter,
    },
    legend: {
      data: chart.series.map((seriesItem) => seriesItem.name),
      selected: legendSelected,
      top: 10,
      textStyle: { color: "#333", fontSize: 12 },
    },
    grid: { left: 15, right: "1%", bottom: 25, top: 50, containLabel: true },
    xAxis: {
      type: "time",
      min: minDate,
      max: maxDate,
      axisLabel: {
        color: "#555",
        fontSize: 10,
        formatter: (value: number) => {
          const d = new Date(value);
          return `${d.getDate()} ${MONTHS[d.getMonth()]}\n${d
            .getHours()
            .toString()
            .padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
        },
      },
      axisLine: { lineStyle: { color: "#ccc" } },
      splitLine: { lineStyle: { color: "#eee" } },
    },
    yAxis: {
      type: "value",
      name: "Ток, А",
      nameTextStyle: {
        color: "#333",
        fontSize: 11,
        fontWeight: "bold",
        align: "left",
        verticalAlign: "top",
        padding: [-10, 0, 0, -30],
      },
      axisLabel: { color: "#555" },
      axisLine: { lineStyle: { color: "#ccc" } },
      splitLine: { lineStyle: { color: "#eee" } },
      min: 0,
      max: dynamicLevel,
      interval: dynamicLevel ? dynamicLevel / 4 : undefined,
    },
    dataZoom: [
      {
        type: "inside",
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        start: zoomStart,
        end: zoomEnd,
        minSpan: 5,
        throttle: 16,
        filterMode: "none",
      },
    ],
    series,
  };
};
